import React from 'react';
import PropTypes from 'prop-types';
import { Block, Link, Flex, Text, Tooltip, Icon, Flag } from '@upsales/components';
import { NumberFormat } from 'App/babel/utils/numberFormat';
import { openDrawer } from 'Services/Drawer';
import { getAddress } from 'App/babel/components/CreateRelation/AddSubaccount/Views/columnParts';
import { trunc } from 'lodash';
import { hasSoftDeployAccess, isAvailable, Feature } from 'App/services/FeatureHelperStandalone';

export default class NameProspecting extends React.Component {
	constructor(props) {
		super(props);

		const t = Tools.$translate;

		this.lang = {
			parentCompany: t('default.parentCompany'),
			headquarters: t('soliditet.headquarters'),
			branch: t('default.branchOffice'),
			city: t('address.city'),
			numEmployees: t('default.numOfEmployees'),
			turnover: t('default.turnover')
		};

		this.state = { ref: null };
	}

	getSubtitle(hasSubAccountsFeature = false) {
		const { account } = this.props;

		const isSubaccountSubtitle = account.isSubaccount && hasSubAccountsFeature;

		if (isSubaccountSubtitle) {
			const address = getAddress(account);

			return address ? (
				<Flex alignItems="center">
					<Flex>
						{address.country ? (
							<Flag className="subaccount-flag" space="mrs" code={address.country.toLowerCase()} />
						) : null}
					</Flex>
					<Tooltip title={this.lang.city}>
						<Text size="sm" color="grey-11">
							{address.city}
						</Text>
					</Tooltip>
				</Flex>
			) : null;
		}
		if (
			!account.visitingCountry &&
			!account.visitingTown &&
			!account.visitingRegionName &&
			!account.postCountry &&
			!account.postTown &&
			!account.postRegionName &&
			!account.registeredCountry &&
			!account.registeredTown &&
			!account.registeredRegionName &&
			!account.revenue &&
			!account.noEmployees &&
			!account.countryCode
		) {
			return null;
		}

		const country =
			account.visitingCountry || account.postCountry || account.registeredCountry || account.countryCode;

		const flag = country ? <Flag space="mrs" code={country.toLowerCase()} /> : null;

		const town = account.visitingTown || account.postTown || account.registeredTown || null;
		const region = account.visitingRegionName || account.postRegionName || account.registeredRegionName || null;

		const addressSpan = (
			<span style={{ verticalAlign: 'middle' }}>
				{flag}
				<Tooltip title={this.lang.city} position="top">
					<span>{town ?? region}</span>
				</Tooltip>
			</span>
		);

		let numEmployees = null,
			turnover = null;

		if (account.headquarters === 1) {
			const numberFormat = new NumberFormat();
			if (account.noEmployees !== null) {
				const numEmployeesText = numberFormat.short(account.noEmployees);
				numEmployees = (
					<span style={{ marginLeft: '10px' }}>
						<i className="fa fa-user" /> {numEmployeesText}
					</span>
				);
			}
			if (account.revenue !== null) {
				const turnoverText = numberFormat.short(account.revenue);
				turnover = (
					<span style={{ marginLeft: '10px' }}>
						<i className="fa fa-bar-chart" /> {turnoverText}
					</span>
				);
			}
		}

		return (
			<span className="subtitle">
				{addressSpan}
				<Tooltip title={this.lang.numEmployees} position="top">
					{numEmployees}
				</Tooltip>
				<Tooltip title={this.lang.turnover} position="top">
					{turnover}
				</Tooltip>
			</span>
		);
	}

	getAdditionalInfo() {
		if (this.props.account.closestGroupMotherOrgnumber === null && this.props.account.groupSize > 1) {
			return <span className="additional-info">{this.lang.parentCompany}</span>;
		} else if (this.props.account.headquarters === 1) {
			return <span className="additional-info">{this.lang.headquarters}</span>;
		} else {
			return <span className="additional-info">{this.lang.branch}</span>;
		}
	}

	setRef = ref => {
		this.setState({ ref });
	};

	render() {
		let overflow = false;
		const { ref } = this.state;
		if (ref) {
			overflow = ref.scrollWidth > ref.offsetWidth;
		}
		const skipAdditionalInfo = this.props.field?.skipAdditionalInfo;
		const hasSubAccountsV2 = hasSoftDeployAccess('SUB_ACCOUNTS_V2');
		const Component = !this.props.createNewAccountRow ? 'div' : 'td';
		const hasSubAccountsFeature =
			isAvailable(Feature.SUB_ACCOUNTS) && hasSubAccountsV2;

		const { account } = this.props;
		const count = account.numberOfSubaccounts ?? 0;
		const langTag = `account.subaccount.count${count > 1 ? '.multi' : ''}`;
		const windowWidth = window.innerWidth;

		const accountName = trunc(account?.name, {
			length: windowWidth > 1500 ? 45 : windowWidth > 1300 ? 35 : 25
		});

		const onClick = e => {
			e.stopPropagation();
			openDrawer('SubaccountDrawer', {
				client: {
					id: account.id,
					name: account.name,
					hasAccess: true
				},
				includingSubaccountData: true
			});
		};

		const res = (
			<Flex alignItems="center">
				{hasSubAccountsFeature && account.numberOfSubaccounts ? (
					<Icon
						space="mrm"
						name={this.props.showSubaccounts ? 'chevron-up' : 'chevron-down'}
						className="chevron-icon"
						onClick={e => {
							e.stopPropagation();
							this.props.onToggleSubaccounts?.();
						}}
					/>
				) : null}
				<Flex direction="column">
					<Flex alignItems="center">
						<Tooltip title={account.name} disabled={account.name === accountName && !overflow}>
							<span className="row-text" ref={this.setRef}>
								{accountName} {skipAdditionalInfo ? null : this.getAdditionalInfo()}
							</span>
						</Tooltip>
						{!hasSubAccountsFeature && account.numberOfSubaccounts ? (
							<Block space="mlm">
								<Text size="sm">
									<Link onClick={onClick}>{Tools.$translate(langTag, { count })}</Link>
								</Text>
							</Block>
						) : null}
						{hasSubAccountsFeature ? (
							<Block space="mlm" backgroundColor="grey-4" borderRadius>
								<Text size="sm" color="grey-11" space="pls prs">
									{account.numberOfSubaccounts && !this.props.showSubaccounts
										? Tools.$translate(langTag, { count })
										: account.isSubaccount
										? Tools.$translate('account.subaccount')
										: null}
								</Text>
							</Block>
						) : null}
					</Flex>

					{this.getSubtitle(hasSubAccountsFeature)}
				</Flex>
			</Flex>
		);
		return this.props.noTable ? (
			<div
				className={`DataSourceColumn ${this.props.field?.className} ${
					!this.props.createNewAccountRow ? 'align-items-center' : ''
				}`}
			>
				{res}
			</div>
		) : (
			<Component
				className={`DataSourceColumn ${this.props.field?.className} ${
					!this.props.createNewAccountRow ? 'align-items-center' : ''
				}`}
			>
				{res}
			</Component>
		);
	}
}

NameProspecting.propTypes = {
	account: PropTypes.object,
	field: PropTypes.object,
	noTable: PropTypes.bool,
	onToggleSubaccounts: PropTypes.func,
	showSubaccounts: PropTypes.bool,
	createNewAccountRow: PropTypes.bool
};
